import React, { useEffect, useState } from "react";
import { config } from "~/core";
import { apiAutoSchedule } from "../api/autoschedule";
import { toast } from "react-toastify";

// Auto-Schedule Predictions (FOU-625, Stage 0.1 dry-run).
// Superuser-only watch surface: shows what the auto-scheduler WOULD do for
// eligible Basic inspections — proposed date, inspector, marginal drive cost,
// resulting fullness, and the offer text it would send. No texts/appointments
// are actually made (dry-run). Route + API enforce IsSuperAdmin.
function AutoSchedulePredictions() {
  const [preds, setPreds] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiAutoSchedule
      .getPredictions()
      .then((res) => {
        if (cancelled) {return;}
        if (res.data && res.data.status === 200) {
          setPreds(res.data.result || []);
        } else {
          toast.error("Failed to load predictions");
        }
      })
      .catch((err) => {
        if (!cancelled) {toast.error("Failed to load predictions");}
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) {setLoading(false);}
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!config.isSuperAdmin) {
    return (
      <div className="tw-p-6 tw-text-sm tw-text-gray-600">
        You need superuser access to view auto-schedule predictions.
      </div>
    );
  }

  if (loading) {
    return <div className="tw-p-6 tw-text-sm tw-text-gray-500">Loading predictions…</div>;
  }

  const hasPreds = preds && preds.length > 0;

  return (
    <div className="tw-p-6">
      <div className="tw-mb-4">
        <h1 className="tw-text-xl tw-font-semibold tw-text-gray-800">Auto-Schedule Predictions</h1>
        <p className="tw-mt-1 tw-text-sm tw-text-gray-500">
          Dry-run preview of what the auto-scheduler would offer homeowners. No texts or
          appointments are made. Updated on intake + daily batch.
        </p>
      </div>

      {!hasPreds && 
        <div className="tw-text-sm tw-text-gray-500 tw-py-8 tw-text-center">
          No predictions yet. Eligible Basic inspections will appear here as they come in.
        </div>
      }

      {hasPreds && 
        <div className="tw-overflow-x-auto">
          <table className="tw-min-w-full tw-text-sm">
            <thead className="tw-bg-gray-50 tw-text-gray-600 tw-uppercase tw-text-xs">
              <tr>
                <th className="tw-px-3 tw-py-2 tw-text-left">Policy</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Proposed Date</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Inspector</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Marginal (min)</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Fullness</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Age (days)</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Offer Text</th>
                <th className="tw-px-3 tw-py-2 tw-text-left">Reason</th>
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-gray-100">
              {preds.map((p) => 
                <tr key={p.policyNo} className="tw-hover:bg-gray-50">
                  <td className="tw-px-3 tw-py-2 tw-font-mono tw-text-xs">{p.policyNo}</td>
                  <td className="tw-px-3 tw-py-2">
                    {p.proposedDate && !p.reason ? formatDate(p.proposedDate) : "—"}
                  </td>
                  <td className="tw-px-3 tw-py-2">{p.proposedInspectorId > 0 && p.inspectorName ? p.inspectorName : "—"}</td>
                  <td className="tw-px-3 tw-py-2">
                    {p.proposedInspectorId > 0 && !p.reason ? p.marginalCostMinutes.toFixed(1) : "—"}
                  </td>
                  <td className="tw-px-3 tw-py-2">
                    {p.proposedInspectorId > 0 && !p.reason ? `${p.fullnessPct.toFixed(0)}%` : "—"}
                  </td>
                  <td className="tw-px-3 tw-py-2">{p.ageDays}</td>
                  <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-600 tw-max-w-xs">
                    {p.offerTextPreview || "—"}
                  </td>
                  <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-amber-700">{p.reason || ""}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function formatDate(s) {
  const d = new Date(s);
  if (isNaN(d)) {return s;}
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export default AutoSchedulePredictions;